import React from 'react';
import {Modal, ModalSize} from './modal';
import {Button, ButtonVariant, ButtonSize} from '../button';
import {Heading, HeadingLevel, HeadingVariant, HeadingWeight, HeadingColor} from '../typography/heading';
import {Text, TextVariant, TextColor} from '../typography/text';
import {Stack, StackSpacing} from '../layout';

export enum ConfirmationVariant {
  DANGER = 'danger',
  WARNING = 'warning',
  INFO = 'info',
}

interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isLoading?: boolean;
  variant?: ConfirmationVariant;
}

export const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  isLoading = false,
  variant = ConfirmationVariant.INFO,
}) => {
  const getConfirmButtonVariant = () => {
    switch (variant) {
      case ConfirmationVariant.DANGER:
        return ButtonVariant.Danger;
      case ConfirmationVariant.WARNING:
        return ButtonVariant.Secondary;
      case ConfirmationVariant.INFO:
        return ButtonVariant.Primary;
      default:
        return ButtonVariant.Primary;
    }
  };

  const handleConfirm = () => {
    if (!isLoading) {
      onConfirm();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={isLoading ? undefined : onClose}
      size={ModalSize.SM}
      preventBackdropClose={isLoading}
      showCloseButton={!isLoading}>
      <div className="p-6">
        <Stack spacing={StackSpacing.MD}>
          <Heading level={HeadingLevel.H3} variant={HeadingVariant.SUBTITLE} weight={HeadingWeight.SEMIBOLD} color={HeadingColor.PRIMARY}>
            {title}
          </Heading>

          <Text variant={TextVariant.BODY} color={TextColor.SECONDARY}>
            {message}
          </Text>

          <div className="flex justify-end space-x-3">
            <Button variant={ButtonVariant.Outline} size={ButtonSize.SM} onClick={onClose} disabled={isLoading}>
              {cancelText}
            </Button>
            <Button variant={getConfirmButtonVariant()} size={ButtonSize.SM} onClick={handleConfirm} disabled={isLoading}>
              {isLoading ? 'Processing...' : confirmText}
            </Button>
          </div>
        </Stack>
      </div>
    </Modal>
  );
};
